import React, { useState } from 'react';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import Button from '../ui/Button';
import Modal, {
  ModalHeader,
  ModalContent,
  ModalFooter,
} from '../ui/Modal';
import FormInput from '../ui/FormInput';

interface AgeGateProps {
  minAge: number;
  onVerify: (verified: boolean) => void;
  isOpen: boolean;
  onClose?: () => void;
  contentTitle?: string;
}

/**
 * Age Gate Component
 * Verifies user age before showing age-restricted content
 */
const AgeGate: React.FC<AgeGateProps> = ({
  minAge,
  onVerify,
  isOpen,
  onClose,
  contentTitle = 'this content',
}) => {
  const [birthDate, setBirthDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const calculateAge = (birthDateString: string): number => {
    const birth = new Date(birthDateString);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleVerify = () => {
    if (!birthDate) {
      setError('Please enter your birth date');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const age = calculateAge(birthDate);
      if (age >= minAge) {
        // Store verification in localStorage
        localStorage.setItem(`ageGate_${minAge}`, JSON.stringify({
          verified: true,
          age,
          timestamp: new Date().toISOString(),
        }));
        onVerify(true);
        if (onClose) onClose();
      } else {
        setError(`You must be ${minAge} or older to view this content.`);
      }
    } catch (error) {
      setError('Invalid birth date. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCancel = () => {
    setBirthDate('');
    setError(null);
    onVerify(false);
    if (onClose) onClose();
  };

  // Check if already verified
  React.useEffect(() => {
    const stored = localStorage.getItem(`ageGate_${minAge}`);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        // Verify that the stored age is still valid (check expires after 30 days)
        const storedDate = new Date(data.timestamp);
        const daysSince = (Date.now() - storedDate.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSince < 30 && data.verified && data.age >= minAge) {
          onVerify(true);
          if (onClose) onClose();
        }
      } catch (e) {
        // Invalid stored data, continue with verification
      }
    }
  }, [minAge, onVerify, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleCancel}>
      <ModalHeader>
        <div className="flex items-center gap-3">
          <LockClosedIcon className="w-6 h-6 text-patreon-orange" />
          <span>Age Verification Required</span>
        </div>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-4">
          <div className="text-center">
            <LockClosedIcon className="w-16 h-16 text-patreon-orange mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              This content is age-restricted
            </h3>
            <p className="text-gray-600 mb-4">
              You must be <strong>{minAge} years or older</strong> to view {contentTitle}.
            </p>
            <p className="text-sm text-gray-500">
              Please verify your age by entering your birth date below.
            </p>
          </div>

          <div className="mt-6">
            <FormInput
              label="Birth Date"
              type="date"
              value={birthDate}
              onChange={(e) => {
                setBirthDate(e.target.value);
                setError(null);
              }}
              required
              max={new Date(new Date().setFullYear(new Date().getFullYear() - minAge)).toISOString().split('T')[0]}
            />
            {error && (
              <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mt-6">
            <p className="text-xs text-gray-600">
              <strong>Privacy Note:</strong> Your birth date is used only for age verification and is not stored or shared.
              Verification is stored locally in your browser and expires after 30 days.
            </p>
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button onClick={handleVerify} loading={isVerifying}>
          Verify Age
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AgeGate;


